import { useState, useEffect } from 'react';
import { dashboardAPI } from '../services/api';
import {
  FiPackage,
  FiTrendingUp,
  FiTrendingDown,
  FiAlertCircle
} from 'react-icons/fi';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const Dashboard = () => {
  const [summary, setSummary] = useState(null);
  const [locationStock, setLocationStock] = useState(null);
  const [categoryStock, setCategoryStock] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [summaryRes, locationRes, categoryRes, invoicesRes, lowStockRes] = await Promise.all([
        dashboardAPI.getSummary(),
        dashboardAPI.getLocationStock(),
        dashboardAPI.getCategoryStock(),
        dashboardAPI.getRecentInvoices(),
        dashboardAPI.getLowStock()
      ]);
      console.log("Category Data Check:", categoryRes.data.data);

      setSummary(summaryRes.data.data);
      setLocationStock(locationRes.data.data);
      setCategoryStock(categoryRes.data.data);
      setRecentInvoices(invoicesRes.data.data);
      setLowStock(lowStockRes.data.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 font-medium">{title}</p>
          <h3 className="text-3xl font-bold text-gray-800 mt-2">{value?.toLocaleString() || 0}</h3>
        </div>
        <div className={`p-4 rounded-full ${bgColor}`}>
          <Icon className={`w-8 h-8 ${color}`} />
        </div>
      </div>
    </div>
  );
  

  const COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
        <p className="text-gray-600 mt-1">Overview of your inventory and sales</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatCard
          title="Total Products"
          value={summary?.total_products}
          icon={FiPackage}
          color="text-blue-600"
          bgColor="bg-blue-100"
        />
        <StatCard
          title="Stock In"
          value={summary?.total_stock_in}
          icon={FiTrendingUp}
          color="text-green-600"
          bgColor="bg-green-100"
        />
        <StatCard
          title="Stock Out"
          value={summary?.total_stock_out}
          icon={FiTrendingDown}
          color="text-red-600"
          bgColor="bg-red-100"
        />
        <StatCard
          title="Current Stock"
          value={summary?.current_stock}
          icon={FiPackage}
          color="text-purple-600"
          bgColor="bg-purple-100"
        />
        <StatCard
          title="Low Stock Alerts"
          value={summary?.low_stock_alerts}
          icon={FiAlertCircle}
          color="text-orange-600"
          bgColor="bg-orange-100"
        />
      </div>

      {/* Location-wise Stock */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Office Stock</h3>
            <div className="p-2 bg-blue-100 rounded-lg">
              <FiPackage className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">{locationStock?.office?.stock || 0}</p>
          <p className="text-sm text-gray-600 mt-1">
            Value: ₹{locationStock?.office?.value?.toLocaleString() || 0}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Godown Stock</h3>
            <div className="p-2 bg-green-100 rounded-lg">
              <FiPackage className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">{locationStock?.godown?.stock || 0}</p>
          <p className="text-sm text-gray-600 mt-1">
            Value: ₹{locationStock?.godown?.value?.toLocaleString() || 0}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Factory Stock</h3>
            <div className="p-2 bg-purple-100 rounded-lg">
              <FiPackage className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">{locationStock?.factory?.stock || 0}</p>
          <p className="text-sm text-gray-600 mt-1">
            Value: ₹{locationStock?.factory?.value?.toLocaleString() || 0}
          </p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Stock Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Stock by Category</h3>
          {/* <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={categoryStock}
                dataKey="stock"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {categoryStock.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer> */}
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie width={300} height={200}
                data={categoryStock.map(item => ({
                  ...item,
                  stock: Number(item.stock)
                }))}
                dataKey="stock"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {categoryStock.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Category Value Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Stock Value by Category</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={categoryStock}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#8B5CF6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Invoices & Low Stock */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Invoices & Stock Out */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Transactions</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b">
                  {/* <th className="text-left py-2 text-sm font-semibold text-gray-600">Type</th> */}
                  <th className="text-left py-2 text-sm font-semibold text-gray-600">#</th>
                  <th className="text-left py-2 text-sm font-semibold text-gray-600">Customer</th>
                  <th className="text-left py-2 text-sm font-semibold text-gray-600">Amount</th>
                  <th className="text-left py-2 text-sm font-semibold text-gray-600">Payment</th>
                  <th className="text-left py-2 text-sm font-semibold text-gray-600">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentInvoices.slice(0, 5).map((item, index) => (
                  <tr key={`${item.source}-${item.id}-${index}`} className="border-b hover:bg-gray-50">
                    {/* <td className="py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        item.source === 'invoice'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {item.source === 'invoice' ? 'Invoice' : 'Stock Out'}
                      </span>
                    </td> */}
                    <td className="py-3 text-sm text-gray-800">{item.invoice_number}</td>
                    <td className="py-3 text-sm text-gray-800">{item.customer_name}</td>
                    <td className="py-3 text-sm text-gray-800">₹{parseFloat(item.amount || 0).toLocaleString()}</td>
                    <td className="py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${item.payment_status === 'paid'
                          ? 'bg-green-100 text-green-800'
                          : item.payment_status === 'partial'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                        {item.payment_status}
                      </span>
                    </td>
                    <td className="py-3 text-sm text-gray-600">
                      {item.date ? new Date(item.date).toLocaleDateString('en-IN') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Low Stock Products */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Low Stock Alert</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-sm font-semibold text-gray-600">Product</th>
                  <th className="text-left py-2 text-sm font-semibold text-gray-600">Stock</th>
                  <th className="text-left py-2 text-sm font-semibold text-gray-600">Threshold</th>
                </tr>
              </thead>
              <tbody>
                {lowStock.slice(0, 5).map((product) => (
                  <tr key={product.id} className="border-b hover:bg-gray-50">
                    <td className="py-3">
                      <p className="text-sm font-medium text-gray-800">{product.name}</p>
                      <p className="text-xs text-gray-500">{product.item_code}</p>
                    </td>
                    <td className="py-3">
                      <span className="text-sm font-semibold text-red-600">
                        {product.total_stock}
                      </span>
                    </td>
                    <td className="py-3 text-sm text-gray-600">{product.low_stock_threshold}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
